import { Link } from "react-router-dom";
import { ChevronRight, Sparkles, Check, ArrowRight, Smartphone, Globe, Cloud } from "lucide-react";
import Reveal from "../components/Reveal";
import { ASSETS } from "../lib/brand";
import CTAStrip from "../components/CTAStrip";

const FEATURES = [
  "Android billing application", "Cloud synchronization", "Web-based management portal",
  "Sales management", "Purchase management", "Inventory control",
  "Customer management", "Supplier management", "Daily sales reports",
  "Profit analysis reports", "Expense management", "Stock reports",
  "GST billing", "User management", "Real-time dashboard",
  "Multi-device access", "Business performance tracking", "Mobile-first design",
];

const IDEAL = [
  "Retail Stores", "Grocery Shops", "Fashion Stores", "Mobile Shops",
  "Hardware Stores", "Medical Stores", "Restaurants & Cafés", "Small Business Owners",
];

const ACCENT = "#00B4D8";

export default function DigiposPrimex() {
  return (
    <div data-testid="digipos-primex-page">
      <section className="relative pt-32 pb-24 overflow-hidden" style={{ background: `linear-gradient(135deg, #0B2545 0%, #1565C0 50%, ${ACCENT} 100%)` }}>
        <div className="absolute inset-0 gi-mesh opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center text-white">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
                <Link to="/" className="hover:text-white">Home</Link><ChevronRight className="h-3 w-3" />
                <Link to="/digipos" className="hover:text-white">DigiPOS</Link><ChevronRight className="h-3 w-3" />
                <span className="text-white">Primex</span>
              </div>
              <span className="gi-pill-dark"><Sparkles className="h-3.5 w-3.5" /> For Small & Medium Retailers</span>
              <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">DigiPOS Primex</h1>
              <p className="font-display text-xl mt-2" style={{ color: "#A6E8F5" }}>Smart Android Billing & Retail Management</p>
              <p className="text-white/75 mt-5 max-w-xl">
                A modern Android-based billing and retail management solution for SME retailers. Combines a fast Android billing app with a powerful web dashboard — manage your business from anywhere.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/contact" data-testid="primex-demo-btn" className="gi-btn-primary">Request Demo <ArrowRight className="h-4 w-4" /></Link>
                <Link to="/hardware" className="gi-btn-ghost">View Hardware</Link>
              </div>
              <div className="mt-7 flex flex-wrap gap-2">
                <span className="font-mono text-xs uppercase tracking-wider px-3 py-1.5 rounded-full bg-white/10 border border-white/15 inline-flex items-center gap-1.5"><Smartphone className="h-3 w-3" /> Android App</span>
                <span className="font-mono text-xs uppercase tracking-wider px-3 py-1.5 rounded-full bg-white/10 border border-white/15 inline-flex items-center gap-1.5"><Globe className="h-3 w-3" /> Web Dashboard</span>
                <span className="font-mono text-xs uppercase tracking-wider px-3 py-1.5 rounded-full bg-white/10 border border-white/15 inline-flex items-center gap-1.5"><Cloud className="h-3 w-3" /> Cloud Synced</span>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={120}>
              <div className="relative gi-float flex justify-center">
                <div className="absolute -inset-6 bg-gradient-to-br from-[#00B4D8]/40 to-transparent blur-3xl rounded-full" />
                <div className="relative rounded-[36px] overflow-hidden border-[10px] border-[#0B2545] shadow-2xl bg-white w-[450px]">
                  <img src={ASSETS.primex} alt="DigiPOS Primex" className="w-full h-auto block" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-widest" style={{ color: ACCENT }}>Capabilities</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-2">Built for fast-moving SME retail</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {FEATURES.map((f, i) => (
              <Reveal key={f} delay={i * 25}>
                <div className="flex items-start gap-3 p-4 rounded-xl border border-[#DDE6F0] bg-[#F5F8FF] hover:bg-white hover:border-[#00B4D8]/40 transition">
                  <span className="h-7 w-7 rounded-md grid place-items-center shrink-0" style={{ background: `${ACCENT}15`, color: ACCENT }}>
                    <Check className="h-4 w-4" />
                  </span>
                  <span className="text-sm">{f}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F5F8FF] gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-3">Ideal For</h2>
          </Reveal>
          <div className="flex flex-wrap gap-3 mt-6">
            {IDEAL.map((i) => (
              <span key={i} className="px-5 py-2.5 rounded-full bg-white border border-[#DDE6F0] hover:border-[#00B4D8] hover:text-[#00B4D8] transition text-sm">{i}</span>
            ))}
          </div>
        </div>
      </section>

      <CTAStrip heading="Run a smarter retail store with Primex" sub="Get a personalised walkthrough of the Primex app + dashboard." />
    </div>
  );
}
